import * as angular from 'angular';
import { Decimal } from 'decimal.js';

(function () {
    'use strict';

    /**
     * @ngdoc directive
     * @name pkpBigDecimalConverter
     * @memberof app
     *
     * @restrict A
     *
     * @description
     * CR 391: Replacement of number input fields with text input fields for prices.
     *
     * HTML5 input fields of type “number” cannot store the precision of numbers occurring on the platform,
     * hence input fields of type “text” will be used while preserving certain characteristics of the
     * HTML5 number input fields which are as follows:
     * <ul>
     *     <li>Specify a minimum value on the input field to allow and prevent negative values.</li>
     *     <li>The input field should only permit numeric values 0 to 9 and a decimal point.</li>
     *     <li>When pasting values into the input field this functionality will not be applied (like with HTML5 number input fields).</li>
     * </ul>
     *
     * @author flzemke.btc
     */
    angular
        .module('app')
        .directive('pkpBigDecimalConverter', pkpBigDecimalConverter);

    function pkpBigDecimalConverter(bigDecimalConverterService) {
        return {
            restrict: 'A',
            require: 'ngModel',
            link: linkFn
        };

        function linkFn(scope, elem, attr, ngModelCtrl) {
            if (!ngModelCtrl) {
                return;
            }

            var NOT_A_VALID_CHAR = 'NOT_A_VALID_CHAR';
            var VALID_FUNCTIONAL_KEY_CHAR_CODES = [
                37, // left
                39, // right
                9, // tab
                8, // backspace
                13, // enter
                36, // home
                35 // end
            ];

            /**
             * Convert the value the user has entered and was converted by {@link convertToViewValue} and turn it into an
             * object that can be sent to the back-end service as it is.
             *
             * @param {string} viewValue The view value that is displayed to the user.
             * @returns {Object}
             */
            function convertToModelValue(viewValue) {
                if (viewValue === '') {
                    return null;
                }

                let valueAsDecimal: any;
                try {
                    valueAsDecimal = new Decimal(viewValue);
                } catch (e) {
                    return undefined;
                }

                let bigDecimal = {};
                bigDecimal[bigDecimalConverterService.VALUE_KEY_NAME] =
                    valueAsDecimal.toString();
                bigDecimal[bigDecimalConverterService.FLAG_KEY_NAME] = true;
                return bigDecimal;
            }

            ngModelCtrl.$parsers.push(convertToModelValue);

            /**
             * Convert the model value to the view value that the user will see in the input field.
             * This works in conjunction with {@link preventInvalidChars} which filters invalid chars before
             * this method converts the value.
             *
             * @param {Object} modelValue The model value.
             * @param {Decimal} modelValue.value The model value.
             * @returns {string} The value the user will see in the input field.
             */
            function convertToViewValue(modelValue) {
                try {
                    return modelValue.value.toString();
                } catch (err) {
                    return '';
                }
            }

            ngModelCtrl.$formatters.push(convertToViewValue);

            /**
             * Prevent entering of chars which are not numbers, minuses or dots (decimal points)
             * by using regular expression.
             * Note that Chrome, probably Opera--maybe others, definitely not Firefox--won't call this method
             * if a functional key like tab or backspace was pressed.
             *
             * @param {Object} e The event from which the just typed char will be taken.
             */
            function preventInvalidChars(e) {
                var charCode = e.which || e.keyCode; // IE compatibility
                var charStr = String.fromCharCode(charCode);

                if (!charStr) {
                    return;
                }

                var sanitizedChar = charStr.replace(
                    /[^0-9\.\-]/g,
                    NOT_A_VALID_CHAR
                );

                if (
                    (sanitizedChar === NOT_A_VALID_CHAR &&
                        !isValidFunctionalKey(charCode)) ||
                    (charStr == '.' &&
                        (e.target.value.toString().match(/\./g) || []).length >=
                            1) ||
                    (charStr == '.' && e.target.value.toString().length < 1) ||
                    (charStr == '-' &&
                        (e.target.value.toString().match(/\-/g) || []).length >=
                            1) ||
                    (charStr == '-' && e.target.value.toString().length >= 1)
                ) {
                    e.preventDefault();
                }
            }

            elem.bind('keypress', preventInvalidChars);

            /**
             * @param {number} charCode
             * @returns {boolean}
             */
            function isValidFunctionalKey(charCode) {
                return VALID_FUNCTIONAL_KEY_CHAR_CODES.indexOf(charCode) !== -1;
            }
        }
    }
})();
